/**
 * "Same entry" matching for the individual-phase merge.
 *
 * Twelve students writing privately produce "ATN", "Acute tubular necrosis",
 * "acute tubular necrois" and "Ischemic ATN"; the merge has to publish one
 * card that counts four listers. Two entries are the same when, with no
 * negation on one side only, any of these holds:
 *
 *   1. equal after normalising (case, punctuation, hyphens, UK/US spelling,
 *      "+ve"/"-ve" and arrows spelled out, number words as digits)
 *   2. a typo apart: one dropped, added or swapped-adjacent letter per word,
 *      never in a word's first three letters (that is where a-/an-/non- live),
 *      never a substitution (nephrotic/nephritic, ureteral/urethral are one
 *      substitution apart and are different things), digits identical, never
 *      hypo- against hyper-, two edits at most
 *   3. both resolve to the same expert entry through the case's aliases,
 *      strictly (the tier the final reveal uses), where the entry's own words
 *      are all accounted for by that expert entry: "BPH" resolves to the
 *      post-renal entry, "bowel obstruction" does not, whatever the alias
 *   4. the same words: for evidence, one phrase's significant words all
 *      inside the other's ("dry mucous membranes" inside "dry mucous
 *      membranes, dry tongue"); for disease names, three quarters shared.
 *      A word swapped for another is a different statement ("left" for
 *      "right", "protein" for "blood", 410 for 320), and negation is scoped
 *      to its clause ("hematuria but no proteinuria" is not "proteinuria but
 *      no hematuria").
 *
 * Clustering: entries with the same expert key join; a typo joins a cluster
 * through any one member; word similarity has to hold against every member
 * (complete link), so a generic phrase cannot glue two specific ones.
 */
import { normalize, fuzzyMatch } from './matching';

// Sign and direction shorthand spelled out before normalising strips it.
const SHORTHAND = [
  [/\+\s*ve\b/gi, ' positive '], [/-\s*ve\b/gi, ' negative '],
  [/(\w)\+(?=\s|$)/g, '$1 positive '], [/(\w)-(?=\s|$)/g, '$1 negative '],
  [/[↑⬆]/g, ' raised '], [/[↓⬇]/g, ' low '],
  [/</g, ' under '], [/>/g, ' over '],
];
// UK spellings to US, applied to the normalised text.
const SPELLING = [
  // "aem" is always the UK form (haem, anaem, uraem, hypovolaem, ischaem).
  [/aem/g, 'em'], [/oedema/g, 'edema'], [/oesoph/g, 'esoph'], [/diarrhoea/g, 'diarrhea'],
  [/pnoea\b/g, 'pnea'], [/aetiol/g, 'etiol'], [/paed/g, 'ped'], [/gynaec/g, 'gynec'],
  [/tumour/g, 'tumor'], [/ischaem/g, 'ischem'],
];
const NUMBER_WORDS = {
  one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
};

/** Normalised text for comparison: hyphens and slashes are spaces, shorthand and spelling unified. */
export function norm(s) {
  let t = (s || '');
  SHORTHAND.forEach(([re, to]) => { t = t.replace(re, to); });
  // A decimal point is kept (as "p") so 2.1 never reads as 21.
  t = normalize(t.replace(/(\d)\.(\d)/g, '$1p$2').replace(/[-–—/]+/g, ' '));
  SPELLING.forEach(([re, to]) => { t = t.replace(re, to); });
  return t.split(' ').map((w) => NUMBER_WORDS[w] || w).join(' ');
}

// Negations must agree on both sides; a negation applies to its whole clause.
const NEGATIONS = new Set(['no', 'not', 'without', 'absent', 'absence', 'lack', 'lacks', 'denies', 'denied', 'never', 'none', 'negative', 'nil', 'non', 'unremarkable']);
// Clauses break at punctuation and at these conjunctions. "Bladder not
// palpable" negates bladder and palpable; "hematuria but no proteinuria"
// negates proteinuria only.
const SCOPE_END = new Set(['but', 'and', 'with', 'however', 'although', 'though', 'plus', 'except']);
const CLAUSE_BREAK = /[,;:()]|\.(?!\d)|\b(?:but|and|with|however|although|though|plus|except)\b/i;
// Grammar only. Clinical words stay significant here: for the merge, a word
// that differs is a different statement.
const FILLER = new Set([
  'the', 'or', 'of', 'to', 'in', 'on', 'for', 'a', 'an', 'is', 'are', 'was', 'were',
  'has', 'have', 'had', 'at', 'by', 'from', 'that', 'this', 'it', 'its', 'his', 'her', 'he', 'she',
  'be', 'been', 'than', 'then', 'so', 'as', 'any', 'some', 'there', 'their', 'also', 'yet',
  ...SCOPE_END,
]);
// What students abbreviate on a board, expanded so "Cr 410" reads as
// "creatinine 410". Lower case, applied per word after normalising.
const ABBREVIATIONS = {
  cr: 'creatinine', k: 'potassium', na: 'sodium', hb: 'hemoglobin', wbc: 'white cell count',
  bp: 'blood pressure', hr: 'heart rate', rr: 'respiratory rate', temp: 'temperature',
  spo2: 'oxygen saturation', sats: 'saturation', o2: 'oxygen', uo: 'urine output',
  mi: 'myocardial infarction', chf: 'congestive heart failure', hf: 'heart failure',
  sob: 'shortness of breath', uti: 'urinary tract infection', aki: 'acute kidney injury',
  ckd: 'chronic kidney disease', htn: 'hypertension', dm: 'diabetes mellitus',
  bph: 'benign prostatic hyperplasia', jvp: 'jugular venous pressure', gcs: 'glasgow coma scale',
  dre: 'digital rectal examination', ecg: 'electrocardiogram', ekg: 'electrocardiogram',
  cxr: 'chest x ray', pmh: 'past medical history', fhx: 'family history', hx: 'history',
  acei: 'ace inhibitor', nsaids: 'nsaid',
};
// Words that turn a statement into a different one when swapped for another
// of the same group. A side may carry one of them on its own.
const DISCRIMINATORS = [
  ['left', 'right'], ['upper', 'lower'], ['acute', 'chronic'], ['new', 'old'],
  ['raised', 'elevated', 'high', 'increased', 'up'], ['low', 'reduced', 'decreased', 'down'],
  ['positive', 'negative'], ['gain', 'loss'], ['proximal', 'distal'], ['anterior', 'posterior'],
  ['systolic', 'diastolic'], ['early', 'late'], ['mild', 'severe'], ['pre', 'post'],
  ['improved', 'improving', 'worsened', 'worsening'], ['unilateral', 'bilateral'],
];
const ROMAN = /^[ivx]+$/;

// A light stem so "hypotensive" and "hypotension", "tachycardic" and
// "tachycardia", "stones" and "stone" compare as one word. Only applied
// where enough of the word survives.
function stem(t) {
  let w = t;
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) w = w.slice(0, -1);
  if (w.length >= 6) {
    for (const suffix of ['ive', 'ion', 'ing', 'ed', 'ic', 'ia']) {
      if (w.endsWith(suffix) && w.length - suffix.length >= 4) { w = w.slice(0, -suffix.length); break; }
    }
  }
  return w;
}

const significant = (t) => t.length >= 2 && !FILLER.has(t);
const qualifier = (t) => /^\d/.test(t) || ROMAN.test(t) || (t.length === 1 && t !== 'a') || /^[a-z]\d+$/.test(t);
const discriminatorGroup = (t) => DISCRIMINATORS.findIndex((g) => g.includes(t));

/**
 * Significant tokens with polarity. Returns [{ t, neg }] in order; a clause
 * holding a negation word has all its significant words negated. "Bladder
 * not palpable" gives bladder-, palpable-; "hematuria but no proteinuria"
 * gives hematuria+, proteinuria-. Takes the raw text: punctuation marks the
 * clauses.
 */
export function polarTokens(text) {
  const out = [];
  (text || '').split(CLAUSE_BREAK).forEach((clause) => {
    const words = norm(clause).split(' ').filter(Boolean);
    const neg = words.some((w) => NEGATIONS.has(w));
    words.forEach((raw) => {
      if (NEGATIONS.has(raw) || SCOPE_END.has(raw)) return;
      const expanded = ABBREVIATIONS[raw] ? ABBREVIATIONS[raw].split(' ') : [raw];
      expanded.forEach((w) => {
        if (qualifier(w)) out.push({ t: w, neg, group: -1 });
        else if (significant(w)) out.push({ t: stem(w), neg, group: discriminatorGroup(w) });
      });
    });
  });
  return out;
}

/** The significant words of a text, negated ones prefixed "!". */
export const tokens = (text) => polarTokens(text).map(({ t, neg }) => (neg ? `!${t}` : t));

const hasNegation = (text) => norm(text).split(' ').some((w) => NEGATIONS.has(w));

// ---- typos ----------------------------------------------------------------

/** The single edit turning `a` into `b`: an insertion/deletion, a swap of two adjacent letters, or a substitution. */
export function singleEdit(a, b) {
  if (a === b || Math.abs(a.length - b.length) > 1) return null;
  if (a.length === b.length) {
    const diffs = [];
    for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) diffs.push(i);
    if (diffs.length === 1) return { type: 'sub', index: diffs[0] };
    if (diffs.length === 2 && diffs[1] === diffs[0] + 1 && a[diffs[0]] === b[diffs[1]] && a[diffs[1]] === b[diffs[0]]) {
      return { type: 'trans', index: diffs[0] };
    }
    return null;
  }
  const [s, l] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  while (i < s.length && s[i] === l[i]) i += 1;
  return s.slice(i) === l.slice(i + 1) ? { type: 'indel', index: i } : null;
}

const opposite = (a, b) => (a.startsWith('hypo') && b.startsWith('hyper')) || (a.startsWith('hyper') && b.startsWith('hypo'));

/**
 * A typo apart, on normalised text: the same words in the same order, each
 * equal or one dropped/added/swapped letter off, past the first three
 * letters, two such words at most; digits identical; no hypo/hyper swap.
 */
export function typoClose(a, b) {
  if (a === b) return true;
  if (a.replace(/\D/g, '') !== b.replace(/\D/g, '')) return false;
  const wa = a.split(' ');
  const wb = b.split(' ');
  if (wa.length !== wb.length) return false;
  let edits = 0;
  for (let i = 0; i < wa.length; i += 1) {
    if (wa[i] === wb[i]) continue;
    if (wa[i].length < 5 || wb[i].length < 5 || opposite(wa[i], wb[i])) return false;
    const e = singleEdit(wa[i], wb[i]);
    if (!e || e.type === 'sub' || e.index < 3) return false;
    edits += 1;
    if (edits > 2) return false;
  }
  return true;
}

// ---- words ----------------------------------------------------------------

/**
 * The same words, on raw text. Both sides negated or neither; every shared
 * word carries the same polarity; numbers, where both sides have them, are
 * the same numbers; no discriminator on one side swapped for another of its
 * group on the other. Then, for evidence (`containment`), the smaller set of
 * words (two at least, and at least two fifths of the larger) lies inside the
 * larger; for names, `threshold` of the union is shared.
 */
export function wordsClose(a, b, { threshold = 0.75, containment = true } = {}) {
  if (hasNegation(a) !== hasNegation(b)) return false;
  const pa = new Map(polarTokens(a).map((x) => [x.t, x]));
  const pb = new Map(polarTokens(b).map((x) => [x.t, x]));
  if (!pa.size || !pb.size) return false;
  const numbers = (m) => [...m.keys()].filter((t) => /^\d/.test(t)).sort().join(' ');
  const na = numbers(pa);
  const nb = numbers(pb);
  if (na && nb && na !== nb) return false;
  const groups = (m) => new Map([...m.values()].filter((x) => x.group >= 0).map((x) => [x.group, x.t]));
  const ga = groups(pa);
  const gb = groups(pb);
  for (const [g, t] of ga) if (gb.has(g) && gb.get(g) !== t) return false;
  let inter = 0;
  for (const [t, x] of pa) {
    if (!pb.has(t)) continue;
    if (pb.get(t).neg !== x.neg) return false;
    inter += 1;
  }
  if (!inter) return false;
  if (containment) {
    const [small, big] = pa.size <= pb.size ? [pa, pb] : [pb, pa];
    if (![...small.keys()].every((t) => big.has(t))) return false;
    // The same words either way ("tachycardic" / "tachycardia", "any fever?"
    // / "fever"); otherwise the shorter phrase must be a real part of the
    // longer one, not one word of five.
    if (small.size === big.size) return true;
    return small.size >= 2 && small.size >= 0.4 * big.size;
  }
  const union = pa.size + pb.size - inter;
  return inter / union >= threshold;
}

// ---- expert resolution ----------------------------------------------------

const wordBoundaryIncludes = (hay, needle) => new RegExp(`(^| )${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}( |$)`).test(hay);

/**
 * Resolves a text to one expert entry, strictly. `primary` is the step's own
 * answer key (the expert differential's diseases, the CDM answer); `secondary`
 * entries carry aliases the primary strings lack (the CDM answer, when
 * merging the differential's diseases), or are the other step's names. A text
 * that only hits a secondary entry maps to the first primary entry that
 * resolves to it in either direction, so "BPH" (a CDM alias) and "Prostatic
 * hypertrophy" (inside the differential's "Bladder outlet obstruction from
 * prostatic hypertrophy") land on the same key.
 *
 * A hit needs (a) a whole-word alias, or the text (two words or more, as
 * written) contained on word boundaries in the expert name, or the expert
 * name contained in the text; and (b) every significant word of the text
 * accounted for by that expert entry (its name, its aliases, and the bridged
 * primary's name). So "obstruction" resolves to the post-renal entry while
 * "bowel obstruction" does not, and "low potassium" never lands on
 * "Hyperkalemia" through the alias "potassium". The first hit wins.
 *
 * The key carries `text`, the name to publish, and `rank`: the step's own
 * answer key names the card where any wording reaches it (2 for a direct
 * hit, 1 through an alias that carries the entry's head word), and only a
 * bare alias falls back to the broader secondary name (0). The cluster keeps
 * the best-ranked name any of its members produced, so the card reads the
 * same whichever student typed first.
 */
export function makeResolver({ primary = [], secondary = [] } = {}) {
  const words = (e) => new Set([e.text, ...(e.aliases || [])].flatMap((s) => tokens(s)));
  const primaryWords = primary.map(words);
  const secondaryWords = secondary.map(words);
  const bridge = secondary.map((sec) => primary.findIndex((p) => fuzzyMatch(p.text, sec) || fuzzyMatch(sec.text, p)));

  const hits = (text, entry) => {
    const t = norm(text);
    const name = norm(entry.text);
    if (!t || !name) return false;
    if (fuzzyMatch(text, { aliases: entry.aliases || [] })) return true;
    if (wordBoundaryIncludes(t, name)) return true;
    return t.split(' ').length >= 2 && wordBoundaryIncludes(name, t);
  };
  const covered = (text, wordSets) => {
    const own = tokens(text).filter((w) => !w.startsWith('!'));
    return own.length > 0 && own.every((w) => wordSets.some((set) => set.has(w)));
  };
  // "Hypovolemia" is the head of "Hypovolemia from diarrhea and poor oral
  // intake" and may name it; "obstruction" is a middle word of "Bladder
  // outlet obstruction from prostatic hypertrophy" and may not, so a bare
  // alias keeps the broader name it actually hit.
  const namesPrimary = (text, index) => {
    const head = tokens(primary[index].text).find((w) => !w.startsWith('!'));
    return !!head && tokens(text).includes(head);
  };

  const memo = new Map();
  return (text) => {
    if (memo.has(text)) return memo.get(text);
    let key = null;
    for (let i = 0; i < primary.length && !key; i += 1) {
      if (hits(text, primary[i]) && covered(text, [primaryWords[i]])) {
        key = { list: 'primary', index: i, text: primary[i].text, rank: 2 };
      }
    }
    for (let i = 0; i < secondary.length && !key; i += 1) {
      if (!hits(text, secondary[i])) continue;
      const p = bridge[i];
      const sets = p >= 0 ? [secondaryWords[i], primaryWords[p]] : [secondaryWords[i]];
      if (!covered(text, sets)) continue;
      const head = p >= 0 && namesPrimary(text, p);
      key = p >= 0
        ? { list: 'primary', index: p, text: head ? primary[p].text : secondary[i].text, rank: head ? 1 : 0 }
        : { list: 'secondary', index: i, text: secondary[i].text, rank: 0 };
    }
    memo.set(text, key);
    return key;
  };
}

const sameKey = (x, y) => !!x && !!y && x.list === y.list && x.index === y.index;

// ---- putting it together --------------------------------------------------

/**
 * `kind`: 'name' for diseases, diagnoses and etiological classes (strict
 * wording); 'evidence' for supporting/opposing and board entries (free text,
 * looser wording). `resolve` comes from makeResolver.
 */
export function sameEntry(a, b, { resolve = null, kind = 'evidence' } = {}) {
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return false;
  if (hasNegation(a) !== hasNegation(b)) return false;
  if (na === nb || typoClose(na, nb)) return true;
  if (resolve && sameKey(resolve(a), resolve(b))) return true;
  return kind === 'name'
    ? wordsClose(a, b, { threshold: 0.75, containment: false })
    : wordsClose(a, b);
}

/**
 * Groups entries ({ text, userKey, ...rest }) into clusters of the same
 * entry. Each cluster: { text, count, users: Set, members: [] }, where
 * `count` is the number of distinct students and `text` is the expert's
 * canonical name when `canonical` is set and the cluster resolved to one,
 * otherwise the longest wording among the members.
 */
export function clusterEntries(entries, { resolve = null, kind = 'evidence', canonical = false } = {}) {
  const clusters = [];
  const wordOpts = kind === 'name' ? { threshold: 0.75, containment: false } : {};
  entries.forEach((e) => {
    const key = resolve ? resolve(e.text) : null;
    const ne = norm(e.text);
    const negE = hasNegation(e.text);
    const home = clusters.find((c) => {
      if (key && sameKey(key, c.key)) return true;
      if (c.members.some((m) => hasNegation(m.text) !== negE)) return false;
      // A typo or the same wording joins through any one member.
      if (c.members.some((m) => { const nm = norm(m.text); return nm === ne || typoClose(nm, ne); })) return true;
      // Word similarity has to hold against every member.
      return c.members.every((m) => wordsClose(m.text, e.text, wordOpts));
    });
    if (home) {
      home.members.push(e);
      if (e.userKey) home.users.add(e.userKey);
      if (key && (!home.key || key.rank > home.key.rank)) home.key = key;
    } else {
      clusters.push({ key, members: [e], users: new Set(e.userKey ? [e.userKey] : []) });
    }
  });
  return clusters.map((c) => {
    let text = c.members.reduce((best, m) => (m.text.trim().length > best.length ? m.text.trim() : best), '');
    if (canonical && c.key) text = c.key.text;
    return { text, count: c.users.size || c.members.length, users: c.users, members: c.members };
  });
}

/**
 * Did a student's text name this expert item? The reveal's matching plus
 * typo tolerance, so the class-gaps count agrees with what the merge just
 * collapsed ("Hypovolemai" counts for Hypovolemia).
 */
export function studentSaid(text, item, opts = {}) {
  if (fuzzyMatch(text, item, opts)) return true;
  const t = norm(text);
  return [item.text, ...(item.aliases || [])].some((s) => typoClose(t, norm(s)));
}
